import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo } from 'react';
import { getAccountActivityRuleErrors } from '../../../../components/InputField/helper';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import { TRANSACTION_FIELDS } from '../IPlushelper';
import DocumentFirstStepHead from './components/DocumentFirstStepHead';
import { VARIANT } from '../../../../util/variant';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import ExpectedActivityTableV2 from './ExpectedActivityTableV2';
import { collectVisibleFieldErrors, getFirstErrorInFieldOrder, getVisibleWizardFieldOrder, mapRuleErrorsToFieldErrors, mergeClearedFieldErrors, omitFieldPathErrors } from './helperV2';

const ABOUT_BUSINESS_FIELDS = [
    "Primary_Business_Activities",
    "Business_Product_Services",
    "Customer_Description",
    "hasBusinessPlan",
    "Illegal_Immoral_Disclosure",
];

const CONVERSION_PLAN_FIELDS = [
    "hasCoinConversionPlan",
    "hasCryptoConversionPlan",
];

const COUNTRY_RISK_FIELDS = [
    "internationalCustomers",
];

const sortFieldOrderWithTemplate = (fieldOrder = [], template = []) => {
    const templateSet = new Set(template);
    return [
        ...template.filter((field) => fieldOrder.includes(field)),
        ...fieldOrder.filter((field) => !templateSet.has(field)),
    ];
};

const BusinessActivityPlus = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    handleChange,
    handlePrev,
    applicationForm,
    pageNum = 2,
    variant,
    stepCount,
    stepLabel,
}) => {
    const isDocFirst = variant === VARIANT.V2;
    const { showError } = useContext(DialogContext);
    const expectedActivityFields = useMemo(() => [
        "Amount_Of_Initial_Deposit",
        ...TRANSACTION_FIELDS,
        "Expected_Amount_Monthly_Digital_Currency_Custody",
    ], []);
    const expectedActivityFieldSet = useMemo(() => new Set(expectedActivityFields), [expectedActivityFields]);
    const visibleFieldOrder = useMemo(() => getVisibleWizardFieldOrder({
        fields: iPlusFields,
        wizard,
        data,
        type: Number(applicationForm?.type),
    }), [applicationForm?.type, data, iPlusFields, wizard]);
    const bankActivityFieldOrder = useMemo(
        () => visibleFieldOrder.filter((field) => (
            !ABOUT_BUSINESS_FIELDS.includes(field)
            && !CONVERSION_PLAN_FIELDS.includes(field)
            && !COUNTRY_RISK_FIELDS.includes(field)
            && !expectedActivityFieldSet.has(field)
        )),
        [expectedActivityFieldSet, visibleFieldOrder],
    );
    const conversionPlanFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => CONVERSION_PLAN_FIELDS.includes(field)),
            CONVERSION_PLAN_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const aboutBusinessFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => ABOUT_BUSINESS_FIELDS.includes(field)),
            ABOUT_BUSINESS_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const countryRiskFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => COUNTRY_RISK_FIELDS.includes(field)),
            COUNTRY_RISK_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const expectedActivityTableFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => expectedActivityFieldSet.has(field)),
            expectedActivityFields,
        ),
        [expectedActivityFieldSet, expectedActivityFields, visibleFieldOrder],
    );
    const renderedFieldOrder = useMemo(() => [
        ...aboutBusinessFieldOrder,
        ...bankActivityFieldOrder,
        ...conversionPlanFieldOrder,
        ...countryRiskFieldOrder,
        ...expectedActivityTableFieldOrder,
    ], [aboutBusinessFieldOrder, bankActivityFieldOrder, conversionPlanFieldOrder, countryRiskFieldOrder, expectedActivityTableFieldOrder]);
    const handleFieldError = (field, errorMsg) => {
        setErrors((prevErrors) => ({
            ...(prevErrors || {}),
            [field]: errorMsg,
        }));
    };

    const checkNext = () => {
        const stepFieldPaths = visibleFieldOrder;
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: Number(applicationForm?.type),
        });
        const { mergedErrors } = mergeClearedFieldErrors(errors, stepFieldPaths, fieldErrors);
        setErrors(mergedErrors);
        const firstError = getFirstErrorInFieldOrder(renderedFieldOrder, mergedErrors);
        if (firstError?.message) {
            setHasError(true);
            showError(firstError.message);
            return false;
        }

        const crossFieldErrors = getAccountActivityRuleErrors(data, {
            depositLocationFields: ["Deposit_Locations"],
            paymentLocationFields: ["Payment_Locations"],
            deferLocationErrorsUntilActivityFieldsFilled: true,
        });
        if (crossFieldErrors.length) {
            setHasError(true);
            const mappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors);
            const orderedMappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors, { prioritizeActivityFields: true });
            const { mergedErrors } = mergeClearedFieldErrors(errors, stepFieldPaths, mappedErrors);
            setErrors(mergedErrors);
            const firstCrossFieldError = getFirstErrorInFieldOrder(renderedFieldOrder, {
                ...mergedErrors,
                ...orderedMappedErrors,
            });
            if (firstCrossFieldError?.message || crossFieldErrors[0]?.message) {
                showError(firstCrossFieldError?.message || crossFieldErrors[0]?.message);
            }
            return false;
        }

        setErrors((prevErrors) => omitFieldPathErrors(prevErrors || {}, stepFieldPaths));
        return true;
    };

    const handleSubmit = () => {
        if (!checkNext()) return;
        setHasError(false);
        const nextStep = applicationForm?.getNextStepForForm?.({
            wizard,
            applicationForm,
            data,
        });
        return handleUpdate({ nextStep });
    };

    return (
        <div data-testid='business-v2-business-activity-plus-step'>
            {isDocFirst ? (
                <DocumentFirstStepHead
                    stepIndex={pageNum}
                    stepCount={stepCount}
                    stepLabel={stepLabel}
                    title={stepLabel || 'Business activity'}
                    intro='What the business does, and who it does it with.'
                    dataTestId='business-v2-business-activity-head'
                />
            ) : (
                <h2 style={{ fontWeight: 'bolder' }} className='pt-3'>Business Activity and Account Usage</h2>
            )}

            <h3 style={{ fontWeight: 'bolder' }} className='pt-3'>About your business</h3>

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={aboutBusinessFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-business-activity-plus-about-fields'
            />

            <h3 style={{ fontWeight: 'bolder' }} className='pt-3'>Account usage</h3>

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={bankActivityFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-business-activity-plus-bank-fields'
            />

            <div className="mt-4">
                <BusinessV2FieldGrid
                variant={variant}
                    fieldOrder={countryRiskFieldOrder}
                    fieldMap={cyclosFields}
                    data={data}
                    asLabel={asLabel}
                    handleChange={handleChange}
                    hasError={hasError}
                    onFieldError={handleFieldError}
                    dataTestId='business-v2-business-activity-plus-country-fields'
                />
            </div>

            <div className="mt-4">
                <BusinessV2FieldGrid
                variant={variant}
                    fieldOrder={conversionPlanFieldOrder}
                    fieldMap={cyclosFields}
                    data={data}
                    asLabel={asLabel}
                    handleChange={handleChange}
                    hasError={hasError}
                    onFieldError={handleFieldError}
                    dataTestId='business-v2-business-activity-plus-conversion-fields'
                />
            </div>

            <ExpectedActivityTableV2
                fieldOrder={expectedActivityTableFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-business-activity-plus-transaction-table'
            />

            {!hideButtons && (
                <div className='mt-4'>
                    <ButtonGroupNew
                        nextText={isDocFirst ? "Continue" : undefined}
                        onNextClick={handleSubmit}
                        onBackClick={() => handlePrev?.()}
                        showPageNum={false}
                        pageNum={pageNum}
                        asLabel={false}
                        loading={loading}
                        step={pageNum}
                        isDisabled={asLabel}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(BusinessActivityPlus);
